"use client";

import { useCallback, useRef, useState } from "react";
import type { AgenticOverview, CreatedKey, DashboardErrorBody, DashboardKey } from "../lib/dashboardTypes";

export type Result<T> = { ok: true; value: T } | { ok: false; message: string; code?: DashboardErrorBody["code"] };

async function call<T>(url: string, init?: RequestInit): Promise<Result<T>> {
  try {
    const res = await fetch(url, {
      ...init,
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      cache: "no-store",
    });
    const body = (await res.json().catch(() => null)) as (T & DashboardErrorBody) | null;
    if (!res.ok) {
      return { ok: false, message: body?.error ?? `Something went wrong (${res.status}).`, code: body?.code };
    }
    return { ok: true, value: body as T };
  } catch {
    return { ok: false, message: "Couldn't reach steampeek. Check your connection and try again." };
  }
}

/** The dashboard's data and the key actions; every action reloads the overview. */
export function useAgenticOverview() {
  const [overview, setOverview] = useState<AgenticOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    const r = await call<AgenticOverview>("/api/agentic/overview");
    if (mine !== seq.current) return;
    setLoading(false);
    if (r.ok) {
      setOverview(r.value);
      setError(null);
    } else {
      setError(r.message);
    }
  }, []);

  const createKey = useCallback(
    async (name: string) => {
      const r = await call<CreatedKey>("/api/agentic/keys", { method: "POST", body: JSON.stringify({ name }) });
      if (r.ok) void load();
      return r;
    },
    [load],
  );

  const renameKey = useCallback(
    async (id: string, name: string) => {
      const r = await call<{ row: DashboardKey }>(`/api/agentic/keys/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      });
      if (r.ok) void load();
      return r;
    },
    [load],
  );

  const revokeKey = useCallback(
    async (id: string) => {
      const r = await call<{ row: DashboardKey }>(`/api/agentic/keys/${encodeURIComponent(id)}`, {
        method: "DELETE",
        body: "{}",
      });
      if (r.ok) void load();
      return r;
    },
    [load],
  );

  return { overview, error, loading, load, createKey, renameKey, revokeKey };
}
